/**
 * Lazy-loaded icons with encapsulated Suspense boundaries.
 */
import React, { Suspense } from 'react';

// oxlint-disable-next-line @atlassian/no-restricted-imports
import { lazyForPaint } from '@atlassian/react-loosely-lazy';

import { withBrandIcon, type BrandIcon } from '../../../icons/with-brand-icon';

const OfficialIcon = lazyForPaint(
	() =>
		import(
			/* webpackChunkName: "@atlassian/logo-third-party/microsoft-outlook" */ '@atlassian/logo-third-party/microsoft-outlook'
		).then((m) => m.MicrosoftOutlookIcon),
	{ moduleId: '@atlassian/logo-third-party/microsoft-outlook' },
);

const LazyOutlookMailIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-outlook-mail" */ './utils').then((m) => m.OutlookMailIcon),
	{ moduleId: '@atlassian/rovo-connectors-config/outlook-mail/icon' },
);

const LazyOutlookMailSearchResultIconComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-outlook-mail-search-result" */ './utils').then(
			(m) => m.OutlookMailSearchResultIcon,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/outlook-mail/search-result-icon' },
);

const LazyOutlookMailIconBorderlessComponent = lazyForPaint(
	() =>
		import(/* webpackChunkName: "icon-outlook-mail-borderless" */ './utils').then(
			(m) => m.OutlookMailIconBorderless,
		),
	{ moduleId: '@atlassian/rovo-connectors-config/outlook-mail/icon-borderless' },
);

/**
 * Exported icons with self-contained Suspense boundaries.
 * Consumers can use these directly without wrapping in LazySuspense.
 */
export const OutlookMailIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyOutlookMailIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'small',
);

export const OutlookMailSearchResultIcon: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyOutlookMailSearchResultIconComponent />
		</Suspense>
	),
	OfficialIcon,
	'medium',
);

export const OutlookMailIconBorderless: BrandIcon = withBrandIcon(
	(): React.JSX.Element => (
		<Suspense fallback={null}>
			<LazyOutlookMailIconBorderlessComponent />
		</Suspense>
	),
	OfficialIcon,
	'xxsmall',
);
